import type { CountedText } from '@fundline/shared/compliance/caps';
import { toComplianceData, toOverride } from '@fundline/shared/compliance/data';
import {
  type ComplianceData,
  type ComplianceDecision,
  evaluateCompliance,
} from '@fundline/shared/compliance/evaluate';
import type { OverrideRow } from '@fundline/shared/compliance/rules';
import type { MessageClass } from '@fundline/shared/compliance/window';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';

/**
 * The sending rules in the browser (M7): the same shared calculator the worker enforces, fed with the
 * same reference data (read through RLS), for previews only. The worker's final gate decides.
 */

export function useComplianceReferenceQuery() {
  return useQuery({
    queryKey: queryKeys.complianceReference(),
    // The worker reloads every 5 minutes too; the data changes only through migrations.
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ComplianceData> => {
      const db = supabase();
      const [states, holidays, areaCodes] = await Promise.all([
        db
          .from('state_rules')
          .select(
            'state, name, timezones, allowed_start, allowed_end, sunday_allowed, sunday_start, max_per_24h, cap_applies_to, verified_by_counsel, source_url, notes',
          ),
        db.from('federal_holidays').select('holiday_date'),
        db.from('area_code_timezones').select('area_code, region, state, timezone'),
      ]);
      const error = states.error ?? holidays.error ?? areaCodes.error;
      if (error) throw new Error(`Couldn't load the sending rules: ${error.message}`);
      return toComplianceData({
        states: states.data ?? [],
        holidays: holidays.data ?? [],
        areaCodes: areaCodes.data ?? [],
      });
    },
  });
}

export interface OverrideRecord extends OverrideRow {
  id: string;
  notes: string | null;
  updatedAt: string;
}

export function useStateOverridesQuery(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.stateOverrides(workspaceId),
    queryFn: async (): Promise<OverrideRecord[]> => {
      const { data, error } = await supabase()
        .from('workspace_state_overrides')
        .select(
          'id, state, allowed_start, allowed_end, sunday_allowed, max_per_24h, cap_applies_to, notes, updated_at',
        )
        .eq('workspace_id', workspaceId)
        .order('state', { ascending: true, nullsFirst: true });
      if (error) throw new Error(`Couldn't load the workspace rules: ${error.message}`);
      return data.map((o) => ({
        ...toOverride(o),
        id: o.id,
        notes: o.notes,
        updatedAt: o.updated_at,
      }));
    },
  });
}

export interface OverrideInput {
  state: string | null;
  allowedStart: string | null;
  allowedEnd: string | null;
  sundayAllowed: boolean;
  maxPer24h: number | null;
  capAppliesTo: MessageClass[];
  notes: string | null;
}

function overrideErrorMessage(err: { code?: string; message: string }): string {
  if (err.code === '23505') return 'There is already a rule for that state. Edit it instead.';
  if (err.code === '23514') {
    return 'Set at least one limit, and make the start time earlier than the end time.';
  }
  if (err.code === '42501') return 'Only admins can change the sending rules.';
  return err.message;
}

/** Admins add, change and remove overrides (RLS; every change is audited by the database). */
export function useStateOverrideMutations(workspaceId: string) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.stateOverrides(workspaceId) });
  const columns = (input: OverrideInput) => ({
    allowed_start: input.allowedStart,
    allowed_end: input.allowedEnd,
    sunday_allowed: input.sundayAllowed,
    max_per_24h: input.maxPer24h,
    cap_applies_to: input.capAppliesTo,
    notes: input.notes,
  });

  const save = useMutation({
    mutationFn: async ({ id, input }: { id: string | null; input: OverrideInput }) => {
      const db = supabase().from('workspace_state_overrides');
      const { error } = id
        ? await db.update(columns(input)).eq('workspace_id', workspaceId).eq('id', id)
        : await db.insert({ workspace_id: workspaceId, state: input.state, ...columns(input) });
      if (error) throw new Error(overrideErrorMessage(error));
    },
    onSettled: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase()
        .from('workspace_state_overrides')
        .delete()
        .eq('workspace_id', workspaceId)
        .eq('id', id);
      if (error) throw new Error(overrideErrorMessage(error));
    },
    onSettled: invalidate,
  });

  return { save, remove };
}

/** Sets or clears a merchant's time zone (admins; the database checks it is one of the candidates). */
export function useSetMerchantTimezone(workspaceId: string, merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (timezone: string | null) => {
      const { data, error } = await supabase().rpc('set_merchant_timezone', {
        p_merchant_id: merchantId,
        p_timezone: timezone as string,
      });
      if (error) throw new Error(error.message.charAt(0).toUpperCase() + error.message.slice(1));
      if (!data) throw new Error("Couldn't change this merchant. Reload and try again.");
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.merchants(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.inbox(workspaceId) }),
      ]),
  });
}

/** Cancels a held text (the sender, the merchant's rep or an admin). False: it already went out. */
export function useCancelMessage(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string) => {
      const { data, error } = await supabase().rpc('cancel_message', { p_message_id: messageId });
      if (error) throw new Error(error.message.charAt(0).toUpperCase() + error.message.slice(1));
      if (!data) throw new Error("It's already on its way, so it can't be cancelled.");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.inbox(workspaceId) }),
  });
}

/** The current time, refreshed every `everyMs` (previews that say "it's 9:40 PM for them"). */
export function useNow(everyMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

export interface MerchantForRules {
  phone: string;
  state: string | null;
  timezone: string | null;
  timezoneSource: string;
}

/**
 * The preview of the worker's decision for one more text of `cls` to this merchant, now. Null while the
 * rules load. Pass the merchant's counted texts of the last 24 hours (from the thread) for the caps.
 */
export function previewDecision(
  merchant: MerchantForRules,
  cls: MessageClass,
  data: ComplianceData,
  overrides: readonly OverrideRow[],
  texts: readonly CountedText[],
  now: Date,
): ComplianceDecision {
  return evaluateCompliance(
    {
      class: cls,
      merchant: {
        phone: merchant.phone,
        state: merchant.state,
        manualZone: merchant.timezoneSource === 'manual' ? merchant.timezone : null,
      },
      data,
      overrides,
      now,
    },
    texts,
  );
}
